import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { CulturalPipe } from '../../pipes/cultural.pipe';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';
import { PlantListTable } from './plant-list-table/plant-list-table';
import { PlantMap } from './plant-map/plant-map';
import { PlantStatusGraph } from './plant-status-graph/plant-status-graph';
import { Savings } from './savings/savings';
import { OverviewTable } from './overview-table/overview-table';

interface FleetHighlight {
  icon: string;
  labelKey: string;
  valueKey: string;
  helperKey: string;
  helperParams?: Record<string, string | number>;
  state: 'success' | 'warning' | 'info';
}

interface HeroMetric {
  labelKey: string;
  value: string;
  suffix?: string;
  trendKey?: string;
  trend?: string;
  trendParams?: Record<string, string | number>;
}

interface EnergyFlowItem {
  icon: string;
  titleKey: string;
  value: string;
  metaKey: string;
  metaParams?: Record<string, string | number>;
}

interface InsightItem {
  icon: string;
  titleKey: string;
  textKey: string;
  tone: 'success' | 'warning' | 'danger' | 'info';
}

@Component({
  selector: 'app-b2b-dashboard',
  imports: [
    MatButtonModule,
    MatCardModule,
    MatMenuModule,
    TranslatePipe,
    CulturalPipe,
    PlantListTable,
    PlantMap,
    PlantStatusGraph,
    Savings,
    OverviewTable
  ],
  templateUrl: './b2b-dashboard.html',
  styleUrl: './b2b-dashboard.scss'
})
export class B2bDashboardPie {
  readonly heroMetrics: HeroMetric[] = [
    { labelKey: 'pie_dashboard.metrics.fleet_output_today', value: '45.32', suffix: 'kWh', trend: '+2.15%' },
    { labelKey: 'pie_dashboard.metrics.total_production', value: '26.5', suffix: 'MWh', trend: '+8.75%' },
    { labelKey: 'pie_dashboard.metrics.co2_avoided', value: '135', suffix: 'kg', trendKey: 'This_Year' },
    {
      labelKey: 'pie_dashboard.metrics.active_systems',
      value: '5',
      trendKey: 'pie_dashboard.metrics.online_count',
      trendParams: { count: 3 }
    }
  ];

  readonly fleetHighlights: FleetHighlight[] = [
    {
      icon: 'bolt',
      labelKey: 'pie_dashboard.highlights.live_production',
      valueKey: 'pie_dashboard.highlights.live_production_value',
      helperKey: 'pie_dashboard.highlights.best_plant',
      helperParams: { plant: 'LQD09' },
      state: 'success'
    },
    {
      icon: 'battery_charging_full',
      labelKey: 'pie_dashboard.highlights.battery_average',
      valueKey: 'pie_dashboard.highlights.battery_average_value',
      helperKey: 'pie_dashboard.highlights.batteries_connected',
      helperParams: { count: 2 },
      state: 'info'
    },
    {
      icon: 'warning',
      labelKey: 'pie_dashboard.highlights.needs_attention',
      valueKey: 'pie_dashboard.highlights.needs_attention_value',
      helperKey: 'pie_dashboard.highlights.abnormal_plant',
      helperParams: { plant: 'HAD18' },
      state: 'warning'
    }
  ];

  readonly energyFlow: EnergyFlowItem[] = [
    {
      icon: 'solar_power',
      titleKey: 'pie_dashboard.energy.solar_generation',
      value: '45.32 kWh',
      metaKey: 'Today'
    },
    {
      icon: 'home_work',
      titleKey: 'pie_dashboard.energy.site_consumption',
      value: '127.55 kWh',
      metaKey: 'pie_dashboard.energy.across_plants',
      metaParams: { count: 5 }
    },
    {
      icon: 'savings',
      titleKey: 'pie_dashboard.energy.estimated_savings',
      value: '0 DKK',
      metaKey: 'pie_dashboard.energy.live_tariff_pending'
    }
  ];

  readonly insights: InsightItem[] = [
    {
      icon: 'task_alt',
      titleKey: 'pie_dashboard.insights.stable_health_title',
      textKey: 'pie_dashboard.insights.stable_health_text',
      tone: 'success'
    },
    {
      icon: 'priority_high',
      titleKey: 'pie_dashboard.insights.abnormal_signal_title',
      textKey: 'pie_dashboard.insights.abnormal_signal_text',
      tone: 'warning'
    },
    {
      icon: 'hub',
      titleKey: 'pie_dashboard.insights.overview_ready_title',
      textKey: 'pie_dashboard.insights.overview_ready_text',
      tone: 'info'
    }
  ];

  constructor(public themeService: CustomizerSettingsService) {}

  scrollTo(elementId: string): void {
    if (typeof document === 'undefined') return;

    document.getElementById(elementId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}
